import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ChevronRight, ChevronDown, UserCog, IdCard, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/common/Logo";
import { useAuth } from "@/context/AuthContext";

// Display-only onboarding steps. NOT clickable — purely a visual flow.
const STEPS = [
  { Icon: UserCog, label: "Set up account" },
  { Icon: IdCard, label: "Complete profile" },
  { Icon: Users, label: "Start connecting" },
];

export default function Welcome() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const name = user?.preferred_first_name || user?.first_name || "there";

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4 py-10">
      <div className="w-full max-w-lg text-center">
        <div className="mb-6 flex justify-center"><Logo size={44} /></div>
        <div className="rounded-2xl border border-border bg-card p-8 shadow-[0_8px_24px_rgba(16,24,40,0.06)]">
          <span className="inline-flex items-center rounded-full bg-teal-50 px-3 py-1 text-xs font-[560] text-teal-700">Welcome to Startup Otter</span>
          <h1 className="mt-4 text-3xl font-[700] tracking-[-0.01em] text-foreground">Hi {name}, let&apos;s get you set up</h1>
          <p className="mt-3 text-muted-foreground">First, complete your account settings. Then you&apos;ll build your profile and start connecting.</p>

          {/* Display-only flow: 3 cards separated by arrows. Stacks vertically on
              mobile (arrows rotate to point down). */}
          <div className="mt-6 flex flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-center sm:gap-1" data-testid="welcome-steps">
            {STEPS.map((step, i) => (
              <React.Fragment key={step.label}>
                <div
                  className="flex flex-1 flex-col items-center rounded-xl border border-border bg-secondary/60 p-4"
                  data-testid={`welcome-step-${i}`}
                >
                  <step.Icon className="h-6 w-6 text-primary" />
                  <div className="mt-2 text-xs font-[600] text-foreground">{step.label}</div>
                </div>
                {i < STEPS.length - 1 && (
                  <div aria-hidden="true" className="flex shrink-0 items-center justify-center text-muted-foreground">
                    <ChevronRight className="hidden h-5 w-5 sm:block" />
                    <ChevronDown className="h-4 w-4 sm:hidden" />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>

          <Button className="mt-7 w-full" size="lg" onClick={() => navigate("/dashboard/account?onboarding=1")} data-testid="welcome-continue-button">
            Complete account settings <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
